import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'fullscreen';
  elevated?: boolean;
  allowMaximize?: boolean;
  className?: string;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'app-modal-surface--sm',
  md: 'app-modal-surface--md',
  lg: 'app-modal-surface--lg',
  xl: 'app-modal-surface--xl',
  '2xl': 'app-modal-surface--2xl',
  '3xl': 'app-modal-surface--3xl',
  '4xl': 'app-modal-surface--4xl',
  fullscreen: 'app-modal-surface--fullscreen',
};

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  elevated = false,
  allowMaximize = false,
  className,
}) => {
  const [maximized, setMaximized] = React.useState(false);

  useEffect(() => {
    if (!open) {
      setMaximized(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const isFull = size === 'fullscreen' || maximized;

  return createPortal(
    <div
      className={cn('app-modal-root', elevated && 'app-modal-root--elevated')}
      role="dialog"
      aria-modal="true"
    >
      <div className="app-modal-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className={cn(
          'app-modal-surface',
          isFull ? 'app-modal-surface--fullscreen' : sizeClasses[size],
          className
        )}
      >
        {(title || allowMaximize) && (
          <div className="app-modal-header">
            {title && subtitle ? (
              <div>
                <h2 className="app-modal-title app-heading-accent">{title}</h2>
                <p className="app-modal-subtitle">{subtitle}</p>
              </div>
            ) : title ? (
              <h2 className="app-modal-title app-heading-accent">{title}</h2>
            ) : (
              <span />
            )}
            <div className="app-modal-header-actions">
              {allowMaximize ? (
                <button
                  type="button"
                  onClick={() => setMaximized((v) => !v)}
                  className="app-modal-icon-btn"
                  aria-label={maximized ? 'Restore' : 'Maximize'}
                >
                  {maximized ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="app-modal-icon-btn"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        <div className="app-modal-body">{children}</div>
        {footer ? <div className="app-modal-footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
};
